/**
 * Expands and collapses the questions held back by the section's "questions
 * shown" limit.
 *
 * The extra items ship in the markup carrying the `hidden` attribute rather than
 * being withheld from the response, so the answers stay in the page for search
 * engines and for anyone using the browser's find-in-page.
 *
 * The class is declared inside the registration guard because two instances of
 * the section on one page each emit this asset, and a browser executes every
 * script tag even when it serves the file from cache. A top level class would
 * throw a redeclaration SyntaxError on the second evaluation.
 */
if (!customElements.get('td-faqs-more')) {
  class TdFaqsMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-td-faqs-more-button]');
      if (!this.button) return;

      this.handleClick = this.handleClick.bind(this);
      this.button.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.handleClick);
    }

    get extras() {
      const section = this.closest('.td-faqs');
      return [...(section?.querySelectorAll('[data-td-faqs-extra]') ?? [])];
    }

    handleClick() {
      const expanded = this.button.getAttribute('aria-expanded') === 'true';
      const items = this.extras;

      items.forEach((item) => {
        item.hidden = expanded;
      });

      this.button.setAttribute('aria-expanded', String(!expanded));
      this.button.textContent = expanded
        ? this.button.dataset.tdLabelMore
        : this.button.dataset.tdLabelLess;

      // Expanding moves the control away from what the visitor was reading, so
      // send focus to the first new question. Collapsing leaves focus on the
      // button, which stays in place.
      if (!expanded) {
        const firstQuestion = items[0]?.querySelector('summary');
        if (firstQuestion) firstQuestion.focus();
      }
    }
  }

  customElements.define('td-faqs-more', TdFaqsMore);
}
